// What a tap does to a line the kitchen already has.
//
// A sent line is the kitchen's. The pad used to let a server change one in place: + and −
// and the bin all edited the line on the screen, and nothing else. The cart held the *same
// objects* as the order, so the order changed with it, the pad compared each line against
// itself, saw no difference, and never offered to send. The kitchen was not told, the bill
// did not move, and no manager was asked. A burger binned on screen was still cooked and
// still charged; one added on screen was never made and never billed.
//
// So a sent line is never edited in place:
//   + is another one of the same dish — a new line that goes on the next send;
//   − and the bin are a void, sent now with a manager's say-so, built from the server's own
//     lines so a half-built next round never travels with it.

/** The cart's own copy of the server's lines. Never the same objects as `order.items`. */
export const adoptLines = (items) => (items || []).map((it) => ({
  ...it,
  modifiers: [...(it.modifiers || [])],
  components: (it.components || []).map((c) => ({ ...c })),
}));

// Set by the server, per line, at the moment it was sent or authorised. None of it belongs
// to a new line: a comp or price override covers the quantity a manager approved, and a new
// line was never approved.
const SERVER_FIELDS = [
  'line_id', 'added_at', 'added_by', 'course', 'revenue', 'revenue_line', 'cost',
  'price_unverified', 'is_meal', 'voided', 'promotion',
  'comp', 'comp_reason', 'comp_by', 'comp_quantity',
  'price_override', 'price_override_reason', 'price_override_by', 'price_override_quantity',
];

/** One more of a sent line's dish, as a new line for the next send. The price is an
 *  estimate until then — the server prices every line from the menu. A comped or
 *  overridden line carries an authorised price, so the estimate falls back to the menu's. */
export const anotherOf = (line, menuItem = null) => {
  const dish = { ...line };
  SERVER_FIELDS.forEach((k) => { delete dish[k]; });
  const authorised = line.comp || line.price_override != null;
  const price = authorised && menuItem ? menuItem.price : line.price;
  return {
    ...dish,
    modifiers: [...(line.modifiers || [])],
    components: (line.components || []).map((c) => ({ ...c })),
    price,
    quantity: 1,
    subtotal: price,
  };
};

/** Are these two lines the same dish made the same way? Then one more of it is a bigger
 *  quantity, not another line: three taps of + on a sent burger were three 1× lines on the
 *  next ticket. */
export const sameDish = (a, b) => {
  const made = (l) => JSON.stringify([
    l.menu_item_id, l.size || null, l.price, l.modifiers || [], (l.notes || '').trim(),
    (l.allergy_notes || '').trim(),
    (l.components || []).map((c) => [c.group, c.menu_item_id, c.quantity || 1]),
  ]);
  return made(a) === made(b);
};

/** + or − on a line that has not been sent: an ordinary change, made without mutating. */
export const changeUnsent = (cart, index, delta) => {
  const line = cart[index];
  if (!line) return cart;
  const quantity = (Number(line.quantity) || 1) + delta;
  if (quantity <= 0) return cart.filter((_, i) => i !== index);
  return cart.map((l, i) => (i === index ? { ...l, quantity, subtotal: l.price * quantity } : l));
};

/** A new line from the dish sheet: one more on a line still waiting to go that is the same
 *  dish made the same way, else a line of its own. Two pints of lager, both "Pint", were two
 *  lines of one (Oct 2026). Never onto a sent, comped or re-priced line. */
export const addOrMore = (cart, line) => {
  const waiting = cart.findIndex((c) => !c.line_id && !c.comp && c.price_override == null && sameDish(c, line));
  return waiting >= 0 ? changeUnsent(cart, waiting, 1) : [...cart, line];
};

/** The order's lines with `n` taken off one of them — what a void sends. From the server's
 *  lines, never the cart: a void goes now, and must not take an unsent round with it. */
export const voidLines = (orderItems, lineId, n) => (orderItems || []).flatMap((l) => {
  if (l.line_id !== lineId) return [l];
  const quantity = (Number(l.quantity) || 1) - n;
  return quantity > 0 ? [{ ...l, quantity, subtotal: l.price * quantity }] : [];
});

/** A line as the order endpoints take it. */
export const toPayloadLine = (item) => ({
  ...(item.line_id ? { line_id: item.line_id } : {}),
  menu_item_id: item.menu_item_id,
  name: item.name,
  price: item.price,
  quantity: item.quantity,
  modifiers: item.modifiers || [],
  notes: item.notes || '',
  allergy_notes: item.allergy_notes || '',
  components: (item.components || []).map((c) => ({
    group: c.group, menu_item_id: c.menu_item_id, quantity: c.quantity || 1,
  })),
  subtotal: item.price * item.quantity,
  size: item.size || null,
  category: item.category,
});

/** What is on the pad but not yet with the kitchen, in money. The server's total only
 * counts what it has been sent, so a server reading the total to a guest mid-round was
 * short by exactly this. */
export function unsentValue(cart) {
  return Math.round((cart || [])
    .filter((l) => !l?.line_id)
    .reduce((sum, l) => sum + (Number(l.subtotal) || (Number(l.price) || 0) * (Number(l.quantity) || 1)), 0) * 100) / 100;
}
